import { handleNetworkResponseAndGetJsonAsync, Network } from './Network'
import { GlobalVars } from '../../global'
import { CreateEventRequest } from './collections/event/createEvent/createEvent_post'
import { CreateEventResponse } from './collections/event/createEvent/createEvent_200'
import { GetEventsResponse } from './collections/event/getListOfEvents/getListOfEvents_200'
import { GetEventResponse } from './collections/event/getEventDetail/getEventDetail_200'
import { UpdateEventRequest } from './collections/event/updateEvent/updateEvent_post'
import { UpdateEventResponse } from './collections/event/updateEvent/updateEvent_200'
import { CreateInvoiceResponse } from './collections/event/createInvoice/createInvoice_200'
import { CreateInvoiceRequest } from './collections/event/createInvoice/createInvoice_post'
import { GetInvoiceResponse } from './collections/event/getInvoice/getInvoice_200'
import { GetEventsForPlanningResponse } from './collections/event/getListOfEventsForPlanning/getListOfEventsForPlanning_200'
import { UpdateEventInPlanningRequest } from './collections/event/updateEventInPlanning/updateEventInPlanningRequest'
import { EventDto, EventInPlanningDto } from './sharedTypes/Event'
import { removeTimezone } from '../../utils/global-utils'
import { OrderState, PaymentState } from './sharedTypes/Common'
import { ForceApprovedEventsSheetSyncResultDto } from './collections/event/forceApprovedEventsSheetSync/forceApprovedEventsSheetSync_200'

export class EventManager {
    static _instance: EventManager | null = null

    static getManager(): EventManager {
        if (this._instance === null) {
            this._instance = new EventManager()
        }
        return this._instance
    }

    async createEventAsync(event: EventDto): Promise<CreateEventResponse> {
        let response: Response | null = null
        const url = `${GlobalVars.SERVER_ENDPOINT}/event/create`
        const body: CreateEventRequest = {
            ...event,
            startsAt: typeof event.startsAt === 'object' ? removeTimezone(event.startsAt) : event.startsAt,
            endsAt: typeof event.endsAt === 'object' ? removeTimezone(event.endsAt) : event.endsAt,
        }
        try {
            response = await Network.fetch(url, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(body),
            })
        } catch (e) {
            throw new Error('Error executing fetch request.')
        }
        if (response) {
            const result: CreateEventResponse = await handleNetworkResponseAndGetJsonAsync(response)
            return result
        } else {
            throw new Error('No response from server returned.')
        }
    }

    async getEventListAsync(EventsFrom?: Date, EventsTo?: Date): Promise<GetEventsResponse> {
        let response: Response | null = null
        const url = `${GlobalVars.SERVER_ENDPOINT}/event${
            (EventsFrom || EventsTo ? '?' : '') +
            (EventsFrom ? `EventsFrom=${removeTimezone(EventsFrom)}` : '') +
            (EventsFrom && EventsTo ? '&' : '') +
            (EventsTo ? `EventsTo=${removeTimezone(EventsTo)}` : '')
        }`
        try {
            response = await Network.fetch(url, {
                method: 'GET',
                credentials: 'include',
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                },
            })
        } catch (e) {
            throw new Error('Error executing fetch request.')
        }
        if (response) {
            const result: GetEventsResponse = await handleNetworkResponseAndGetJsonAsync(response)
            return result
        } else {
            throw new Error('No response from server returned.')
        }
    }

    async getEventListForPlanningAsync(
        EventsFrom?: Date,
        EventsTo?: Date,
        OrderState?: OrderState,
        PaymentState?: PaymentState
    ): Promise<GetEventsForPlanningResponse> {
        let response: Response | null = null
        const url = `${GlobalVars.SERVER_ENDPOINT}/event/planning${
            (EventsFrom || EventsTo || OrderState || PaymentState ? '?' : '') +
            (EventsFrom ? `EventsFrom=${removeTimezone(EventsFrom)}` : '') +
            (EventsFrom && EventsTo ? '&' : '') +
            (EventsTo ? `EventsTo=${removeTimezone(EventsTo)}` : '') +
            ((EventsFrom || EventsTo) && OrderState ? '&' : '') +
            (OrderState ? `OrderState=${OrderState}` : '') +
            ((EventsFrom || EventsTo || OrderState) && PaymentState ? '&' : '') +
            (PaymentState ? `PaymentState=${PaymentState}` : '')
        }`
        try {
            response = await Network.fetch(url, {
                method: 'GET',
                credentials: 'include',
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                },
            })
        } catch (e) {
            throw new Error('Error executing fetch request.')
        }
        if (response) {
            const result: GetEventsForPlanningResponse = await handleNetworkResponseAndGetJsonAsync(response)
            return result
        } else {
            throw new Error('No response from server returned.')
        }
    }

    async updateEventForPlanningAsync(id: string, event: UpdateEventInPlanningRequest): Promise<EventInPlanningDto> {
        let response: Response | null = null
        const url = `${GlobalVars.SERVER_ENDPOINT}/event/${id}/planning/update`
        const body = event
        try {
            response = await Network.fetch(url, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(body),
            })
        } catch (e) {
            throw new Error('Error executing fetch request.')
        }
        if (response) {
            const result: EventInPlanningDto = await handleNetworkResponseAndGetJsonAsync(response)
            return result
        } else {
            throw new Error('No response from server returned.')
        }
    }

    async getEventAsync(id: string): Promise<GetEventResponse> {
        let response: Response | null = null
        const url = `${GlobalVars.SERVER_ENDPOINT}/event/${id}`
        try {
            response = await Network.fetch(url, {
                method: 'GET',
                credentials: 'include',
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                },
            })
        } catch (e) {
            throw new Error('Error executing fetch request.')
        }
        if (response) {
            const result: GetEventResponse = await handleNetworkResponseAndGetJsonAsync(response)
            return result
        } else {
            throw new Error('No response from server returned.')
        }
    }

    async deleteEventAsync(id: string): Promise<void> {
        let response: Response | null = null
        const url = `${GlobalVars.SERVER_ENDPOINT}/event/${id}`
        try {
            response = await Network.fetch(url, {
                method: 'DELETE',
                credentials: 'include',
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                },
            })
        } catch (e) {
            throw new Error('Error executing fetch request.')
        }
        if (response) {
            const result: void = await handleNetworkResponseAndGetJsonAsync(response)
            return result
        } else {
            throw new Error('No response from server returned.')
        }
    }

    async updateEventAsync(id: string, event: EventDto): Promise<UpdateEventResponse> {
        let response: Response | null = null
        const url = `${GlobalVars.SERVER_ENDPOINT}/event/update/${id}`
        const body: UpdateEventRequest = {
            ...event,
            startsAt: typeof event.startsAt === 'object' ? removeTimezone(event.startsAt) : event.startsAt,
            endsAt: typeof event.endsAt === 'object' ? removeTimezone(event.endsAt) : event.endsAt,
        }
        try {
            response = await Network.fetch(url, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(body),
            })
        } catch (e) {
            throw new Error('Error executing fetch request.')
        }
        if (response) {
            const result = await handleNetworkResponseAndGetJsonAsync(response)
            if (result.errorCode === 1001 && response.status === 400) throw new Error('Událost upravena bez  odesílání emailu.')
            if (result.errorCode === 3000 && response.status === 400)
                throw new Error('Událost upravena bez propojení s kalendářem.')
            return result
        } else {
            throw new Error('No response from server returned.')
        }
    }

    async createInvoiceAsync(id: string, invoice: CreateInvoiceRequest): Promise<CreateInvoiceResponse> {
        let response: Response | null = null
        const url = `${GlobalVars.SERVER_ENDPOINT}/event/${id}/invoice/create`
        const body: CreateInvoiceRequest = invoice

        try {
            response = await Network.fetch(url, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(body),
            })
        } catch (e) {
            throw new Error('Error executing fetch request.')
        }
        if (response) {
            const result: CreateInvoiceResponse = await handleNetworkResponseAndGetJsonAsync(response)
            return result
        } else {
            throw new Error('No response from server returned.')
        }
    }
    async getInvoiceAsync(id: string): Promise<GetInvoiceResponse> {
        let response: Response | null = null
        const url = `${GlobalVars.SERVER_ENDPOINT}/event/${id}/invoice`
        try {
            response = await Network.fetch(url, {
                method: 'GET',
                credentials: 'include',
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                },
            })
        } catch (e) {
            throw new Error('Error executing fetch request.')
        }
        if (response) {
            const result: GetInvoiceResponse = await handleNetworkResponseAndGetJsonAsync(response)
            return result
        } else {
            throw new Error('No response from server returned.')
        }
    }

    async forceApprovedEventsSheetSyncAsync(): Promise<ForceApprovedEventsSheetSyncResultDto> {
        let response: Response | null = null
        const url = `${GlobalVars.SERVER_ENDPOINT}/event/force-approved-events-sheet-sync`
        try {
            response = await Network.fetch(url, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                },
            })
        } catch (e) {
            throw new Error('Error executing fetch request.')
        }
        if (response) {
            const result: ForceApprovedEventsSheetSyncResultDto = await handleNetworkResponseAndGetJsonAsync(response)
            return result
        } else {
            throw new Error('No response from server returned.')
        }
    }
}
